<template>
  <div class="d-flex align-items-center bulk-div">
    <strong class="me-2 bulk-title">{{ $t("bulkUpdate") }}</strong>
    <!-- AppGroup mode toolbar -->
    <div v-if="mode === 'appGroup'" class="right-side d-flex align-items-center ms-auto">
      <span class="action-title">{{ $t("status") }}</span>
      <Dropdown class="bulk_dropdown" style="width: 100px;" v-model="enabledModel" :options="enabledOptions"
        optionLabel="label" optionValue="value" :placeholder="$t('select')" :showClear="true" />
      <span class="sep" />

      <span class="action-title">{{ $t("audit2") }}</span>
      <Dropdown class="bulk_dropdown" v-model="auditModel" :options="auditOptions" optionLabel="label"
        optionValue="value" :placeholder="$t('select')" />
      <span class="sep" />
      <span class="action-title">{{ $t("discoveryMode") }}</span>
      <MultiSelect v-model="discoveryModel" :options="discoveryOptionsFiltered" optionLabel="label" optionValue="value"
        :placeholder="$t('select')" :maxSelectedLabels="1" :showToggleAll="false" appendTo="self"
        style="background-color: transparent;">
        <template #header="{ value, options }">
          <div class="p-multiselect-header d-flex align-items-center justify-content-between w-100">
            <div class="d-flex align-items-center">
              <Checkbox :binary="true" :modelValue="isAllSelected(value, options)"
                @update:modelValue="(checked) => toggleAll(checked, options)" />
              <span class="ms-2">{{ $t('enableAll') }}</span>
            </div>
          </div>
        </template>
      </MultiSelect>

      <div class="flex-grow-1"></div>
      <div class="bulk-actions-buttons" role="group" aria-label="bulk-actions">
        <button class="btn btn-sm bulk-action-btn cancel-section" @click="cancelSection">
          {{ $t("cancel") }}
        </button>
        <button class="btn btn-sm bulk-action-btn" :disabled="!hasChanges" @click="confirmApply">
          <i class="ag-icon ag-icon_apply_changes mr-1"></i>
          {{ $t("applyChanges") }}
        </button>
        <span class="sep" style="margin: 0 4px;"></span>
        <button class="btn btn-sm bulk-action-btn more-actions-btn" @click="toggleMoreActions" ref="moreActionsBtn">
          <span>{{ $t("moreActions") }}</span>
          <i :class="['chevron', isMenuOpen ? 'pi pi-chevron-up' : 'pi pi-chevron-down']" style="font-size: 10px; margin-left: 8px; color:#003060;"></i>
          <!-- <i class="pi pi-chevron-down" style="font-size: 10px; margin-left: 8px;"></i> -->
        </button>
      </div>
      <Menu ref="moreActionsMenu" :model="moreActionsItems" :popup="true" @show="isMenuOpen = true" @hide="isMenuOpen = false" />
    </div>

    <!-- Process mode toolbar -->
    <div v-else-if="mode === 'process'" class="right-side d-flex align-items-center ms-auto">
      <span class="action-title">{{ $t('status') }}</span>
      <Dropdown class="bulk_dropdown" style="width: 100px;" v-model="enabledModel" :options="enabledOptions"
        optionLabel="label" optionValue="value" :placeholder="$t('select')" :showClear="true" />
      <span class="sep" />
      <span class="action-title">{{ $t('processDiscoveryMode') }}</span>
      <Dropdown class="bulk_dropdown" style="width: 100px;" v-model="processDiscoveryMode"
        :options="processDiscoveryOptions" optionLabel="label" optionValue="value" :placeholder="$t('select')"
        :showClear="true" />
      <div class="flex-grow-1"></div>
      <div class="bulk-actions-buttons" role="group" aria-label="bulk-actions">
        <button class="btn btn-sm bulk-action-btn cancel-section" @click="cancelSection">
          {{ $t("cancel") }}
        </button>
        <button class="btn btn-sm bulk-action-btn" :disabled="!hasChanges" @click="confirmApply">
          <i class="ag-icon ag-icon_apply_changes mr-1"></i>
          {{ $t("applyChanges") }}
        </button>
        <button class="btn btn-sm bulk-action-btn delete-btn" @click="confirmDelete('rules')">
          <i class="ag-icon ag-icon_red_delete mr-1"></i>
          {{ $t("delete") }}
        </button>
      </div>
    </div>

    <div v-else class="right-side d-flex align-items-center ms-auto">
      <span class="action-title">{{ $t('status') }}</span>
      <Dropdown class="bulk_dropdown" style="width: 100px;" v-model="enabledModel"
        :options="enabledOptions" optionLabel="label" optionValue="value" :placeholder="$t('select')" :showClear="true" />
      <div class="flex-grow-1"></div>
      <div class="bulk-actions-buttons" role="group" aria-label="bulk-actions">
        <button class="btn btn-sm bulk-action-btn cancel-section" @click="cancelSection">
          {{ $t("cancel") }}
        </button>
        <button class="btn btn-sm bulk-action-btn" :disabled="!hasChanges" @click="confirmApply">
          <i class="ag-icon ag-icon_apply_changes mr-1"></i>
          {{ $t("applyChanges") }}
        </button>
        <button class="btn btn-sm bulk-action-btn delete-btn" @click="confirmDelete('rules')" :disabled="disabledDelete">
          <i class="ag-icon ag-icon_red_delete mr-1"></i>
          {{ $t("delete") }}
        </button>
      </div>
    </div>
  </div>

  <Transition name="modal-fade-scale">
    <BulkConfirmModal v-model="showNoteModal" v-model:note="noteDraft" :title="modalTitle" :maxlength="1000"
      :action="modalAction" :count="selectionCount" :info="modalInfo" :confirmText="modalConfirmText"
      :showTextarea="showTextarea" :requireNote="requireNote" @save="onModalConfirm" @cancel="cancelNote" />
  </Transition>
</template>


<script setup>
import { computed, defineProps, defineEmits, ref } from 'vue'
import MultiSelect from 'primevue/multiselect'
import Dropdown from 'primevue/dropdown'
import Menu from 'primevue/menu'
import Checkbox from 'primevue/checkbox'
import ToggleSwitch from '../ag-edit-panel/forms/components/toggle-switch.vue'
import { useStore } from "@/store";
import BulkConfirmModal from '../ag-edit-panel/forms/components/bulkConfirmModal.vue'
const store = useStore();
import { useI18n } from 'vue-i18n';
import Swal from 'sweetalert2';
const { t } = useI18n();
const props = defineProps({
  bulk: { type: Object, required: true },
  realOptions: { type: Array, default: () => [] },
  disabledApply: { type: Boolean, default: false },
  disabledDelete: { type: Boolean, default: false },
  disabledReset: { type: Boolean, default: false },
  selectionCount: { type: Number, default: 0 },
  mode: { type: String, default: 'appGroup' } // 'appGroup' | 'process' | 'rules'
})

const emit = defineEmits(['update:bulk', 'apply', 'delete', 'reset', 'applyNote', 'cancelSection'])
const auditOptions = computed(() => store.state.general.enums['AppGroupAudit'] || [])

const NONE_FLAG = 32
const discoveryOptions = computed(() => store.state.general.enums['DiscoveryMode'] || [])
const realOptions = computed(() => discoveryOptions.value.filter(opt => opt.label !== 'Enable All' && String(opt.label).toLowerCase() !== 'none'))
const discoveryOptionsFiltered = computed(() => {
  const opts = Array.isArray(realOptions.value) ? realOptions.value : []
  return opts.filter(o => Number(o?.value) !== NONE_FLAG)
})
const isAllSelected = (value, options) => {
  const selected = Array.isArray(value) ? value : [];
  return options && selected.length === options.length && options.length > 0;
};

const toggleAll = (checked, options) => {
  if (!Array.isArray(options)) return;
  discoveryModel.value = checked ? options.map(o => o.value) : [];
};
const enabledTouched = ref(false)
const processDiscoveryTouched = ref(false)
const enabledModel = computed({
  get: () => (props.bulk?.enabled ?? null),
  set: (val) => {
    enabledTouched.value = true
    emit('update:bulk', { ...(props.bulk || {}), enabled: (val === null ? undefined : !!val) })
  }
})
const auditModel = computed({
  get: () => props.bulk?.audit ?? null,
  set: (val) => emit('update:bulk', { ...(props.bulk || {}), audit: val })
})
const discoveryModel = computed({
  get: () => {
    const dm = props.bulk?.discoveryModes
    if (Array.isArray(dm)) {
      return dm.map(Number).filter(v => v !== NONE_FLAG)
    }
    const mask = Number(dm ?? 0)
    const values = discoveryOptionsFiltered.value.map(o => o.value)
    return values.filter(v => (Number(v) & mask) === Number(v))
  },
  set: (arr) => {
    const picked = (Array.isArray(arr) ? arr.map(Number) : []).filter(v => v !== NONE_FLAG)
    emit('update:bulk', { ...(props.bulk || {}), discoveryModes: picked })
  }
})
const processDiscoveryOptions = [
  { label: t('on'), value: true },
  { label: t('off'), value: false }
]
const enabledOptions = [
  { label: t('on'), value: true },
  { label: t('off'), value: false }
]
const processDiscoveryMode = computed({
  get: () => (props.bulk?.processDiscovery ?? null),
  set: (val) => {
    processDiscoveryTouched.value = true
    emit('update:bulk', { ...(props.bulk || {}), processDiscovery: (val === null ? undefined : !!val) })
  }
})

const expertNotesModel = computed({
  get: () => props.bulk?.expertNotes ?? '',
  set: (val) => emit('update:bulk', { ...(props.bulk || {}), expertNotes: val || '' })
})

const showNoteModal = ref(false)
const noteDraft = ref('')
const modalTitle = ref('')
const modalAction = ref('apply')
const modalConfirmText = ref('')
const modalInfo = ref('')
const showTextarea = ref(true)
const requireNote = ref(false)
const moreActionsMenu = ref(null)
const moreActionsBtn = ref(null)
const isMenuOpen = ref(false)
const menu = ref()
const moreActionsItems = computed(() => {
  const items = []
  
  if (props.mode === 'appGroup') {
    items.push({
      label: t('addNote'),
      command: () => openNoteModal()
    })
    items.push({
      label: t('resetToDefault'),
      command: () => confirmReset(),
      disabled: props.disabledReset
    })
  }
  
  // Add delete button at the end
  items.push({
    label: t('delete'),
    command: () => {
      if (props.mode === 'appGroup') {
        confirmDelete('appGroup')
      } else {
        confirmDelete('rules')
      }
    },
    disabled: props.disabledDelete
  })
  
  return items
})

function openNoteModal() {
  noteDraft.value = expertNotesModel.value || ''
  modalTitle.value = t('addNote')
  modalAction.value = 'apply'
  modalConfirmText.value = t('save')
  modalInfo.value = 'Note'
  showTextarea.value = true
  requireNote.value = true
  showNoteModal.value = true
}
function cancelNote() { showNoteModal.value = false }

function cancelSection() {
  emit('cancelSection')
}

function toggleMoreActions(event) {
  if (moreActionsMenu.value) {
    moreActionsMenu.value.toggle(event)
  }
}

async function confirmApply() {
  const pluralS = computed(() => (props.selectionCount === 1 ? '' : 's'))
  const result = await Swal.fire({
    title: t('confirmApply'),
    icon: "question",
    html: `${props.selectionCount} ${t('item')}${pluralS.value} ${t('willBeUpdated')}<br/>${t('pleaseReviewBeforeApplyingChanges')}`,
    showCancelButton: true,
    confirmButtonText: t('apply'),
    cancelButtonText: t('cancel'),
  });
  if (result.isConfirmed) {
    emit('apply')
  }
}
async function confirmDelete(type) {
  const pluralS = computed(() => (props.selectionCount === 1 ? '' : 's'))
  const typeText = computed(() => (type === 'appGroup' ? t('appGroup') : t('rule')))
  const result = await Swal.fire({
    title: t('confirmDelete'),
    icon: "warning",
    html: `${props.selectionCount} ${typeText.value}${pluralS.value} ${t('willBeDeleted')}. <br/> ${t('thisActionCannotBeUndone')} ${t('pleaseConfirmYouWantToProceed')}`,
    showCancelButton: true,
    confirmButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });
  if (result.isConfirmed) {
    emit('delete')
  }
}
async function confirmReset() {
  const pluralS = computed(() => (props.selectionCount === 1 ? '' : 's'))
  const result = await Swal.fire({
    title: t('confirmReset'),
    icon: "warning",
    html: `${props.selectionCount} ${t('appGroup')}${pluralS.value} ${t('willBeReset')}<br/>${t('thisWillOverwriteCurrentSettingsForTheSelectedAppGroup')}`,
    showCancelButton: true,
    confirmButtonText: t('reset'),
    cancelButtonText: t('cancel'),
  });
  if (result.isConfirmed) {
    emit('reset')
  }
}

function onModalConfirm(noteValue) {
  if (showTextarea.value) {
    expertNotesModel.value = noteValue || ''
    if (enabledTouched.value === false) {
      emit('update:bulk', { ...(props.bulk || {}), enabled: null })
    }
    emit('applyNote', noteValue)
    showNoteModal.value = false
    return
  }
  showNoteModal.value = false
  if (modalAction.value === 'delete') return emit('delete')
  if (modalAction.value === 'reset') return emit('reset')
  if (enabledTouched.value === false) {
    emit('update:bulk', { ...(props.bulk || {}), enabled: null })
  }
  return emit('apply')
}

const hasChanges = computed(() => {
  const notes = (props.bulk?.expertNotes || '').trim()
  const hasNotes = notes.length > 0
  const hasAudit = props.bulk?.audit !== null && props.bulk?.audit !== undefined
  const hasDiscovery = Array.isArray(props.bulk?.discoveryModes) && props.bulk.discoveryModes.length > 0

  if (props.mode === 'appGroup') {
    return enabledTouched.value || hasAudit || hasDiscovery || hasNotes
  }
  if (props.mode === 'process') {
    return enabledTouched.value || processDiscoveryTouched.value
  }
  return enabledTouched.value
})
</script>
<style scoped>
.bulk-div {
  background: #EEF7FF;
  /* border: 1px solid #0C4F93; */
  font-size: 12px;
  min-height: 44px;
  width: 100%;
  box-sizing: border-box;
  padding-left: 20px !important;
  padding-right: 0 !important;
}

.action-title {
  font-weight: 600;
  font-size: 12px;
  line-height: 19px;
  text-transform: capitalize;
  color: #485A6D;
  margin-right: 5px;
}

.bulk_dropdown {
  /* width: 140px; */
  background-color: transparent;
}

.bulk_dropdown :deep(.p-dropdown-label) {
  font-size: 12px;
  padding: 0.25rem 0.25rem !important;
}

.sep {
  display: inline-block;
  width: 1px;
  height: 20px;
  background: #D2D9E2;
  margin: 0 12px;
}

.bulk-actions-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-left: 20px;
}

.bulk-action-btn {
  background-color: #4B7CB3;
  color: #fff;
  border: 1px solid #4B7CB3;
  border-radius: 4px;
  display: flex;
  align-items: center;
  font-size: 12px;
  padding: 6px 12px;
  transition: all 0.2s ease;
}

.bulk-action-btn:hover:not(:disabled) {
  background-color: #3d6a9a;
  border-color: #3d6a9a;
}

.bulk-action-btn:disabled {
  background-color: #9db3cc;
  border-color: #9db3cc;
  cursor: not-allowed;
  opacity: 0.6;
}

.bulk-action-btn.cancel-section {
  background-color: transparent;
  color: #003060;
  border-color: #4B7CB3;
}

.bulk-action-btn.cancel-section:hover {
  background-color: #f0f4f8;
}
.bulk-action-btn.delete-btn {
  background-color: transparent;
  color: #DE3232;
  border-color: #E36D6D;
}

.bulk-action-btn.delete-btn:hover {
  background-color: #f0f4f8;
  color: #DE3232;
  border-color: #DE3232;
}
.bulk-action-btn.more-actions-btn {
  background-color: transparent;
  color: #003060;
  border-color: #4B7CB3;
}

.bulk-action-btn.more-actions-btn:hover {
  background-color: #f0f4f8;
  color: #003060;
  border-color: #4B7CB3;
}

.bulk-title {
  color: #0C4F93;
  margin-right: 0;
}

.right-side {
  margin-left: auto;
  justify-content: flex-end;
  height: stretch;
  padding-right: 1rem;
}

.bulk-div :deep(.status-text) {
  font-size: 12px !important;
}

.bulk-div :deep(.p-multiselect-label) {
  font-size: 12px !important;
}

@media (max-width: 1280px) {
  .bulk-div {
    font-size: 10px;
  }

  .action-title {
    font-size: 10px;
  }

  .bulk-actions-buttons {
    margin-left: 5px;
    gap: 6px;
  }

  .bulk-action-btn {
    font-size: 10px;
    padding: 4px 8px;
  }

  .bulk-div :deep(.p-multiselect-label) {
    font-size: 10px !important;
  }

  .bulk_dropdown :deep(.p-dropdown-label) {
    font-size: 10px;
  }
}

:deep(.p-multiselect-panel .p-multiselect-header) {
  padding: 0.75rem 1.5rem 0 1.5rem;
  border-bottom: none;
  background-color: transparent;
  color: #4b5563 !important;
  font-size: 1rem !important;
}

.note-trigger {
  cursor: pointer;
  user-select: none;
  transition: transform 0.08s ease, opacity 0.12s ease, background-color 0.12s ease;
  border-radius: 6px;
  padding: 2px 4px;
}

.note-trigger:hover {
  background-color: rgba(0, 85, 166, 0.06);
}

.note-trigger:active {
  transform: scale(0.98);
  opacity: 0.8;
}

.note-trigger:focus-visible {
  outline: 2px solid #0C4F93;
  outline-offset: 2px;
}

.modal-fade-scale-enter-active,
.modal-fade-scale-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.modal-fade-scale-enter-from,
.modal-fade-scale-leave-to {
  opacity: 0;
  transform: scale(1);
}

.modal-fade-scale-enter-to,
.modal-fade-scale-leave-from {
  opacity: 1;
  transform: scale(1);
}

.more-actions-btn {
  position: relative;
}

</style>
<style>
.p-menu {
  border: 1px solid #D2D9E2 !important;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12) !important;
  border-radius: 10px !important;
  overflow: hidden;
}

.p-menu .p-menuitem-link {
  padding: 0.75rem 1rem !important;
  color: #0B1736 !important;
  font-weight: 600;
}

.p-menu .p-menuitem-link .p-menuitem-icon {
  margin-right: 0.5rem;
}

.p-menu .text-danger {
  color: #C62828 !important;
}
</style>
